import { useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeContext } from "../../contexts/ThemeProvider";
import { LanguageContext } from "../../contexts/LanguageProvider";

const BENEFIT_TABS = ["users", "employers"] as const;
type BenefitTab = (typeof BENEFIT_TABS)[number];

export default function BenefitsSection() {
  const [activeTab, setActiveTab] = useState<BenefitTab>("users");
  const { colorsObj } = useContext(ThemeContext);
  const { t } = useContext(LanguageContext);

  const renderBenefits = () => {
    if (activeTab === "users") {
      return [
        {
          title: t("benefits-user-1"),
          description: t("benefits-user-1-desc"),
        },
        {
          title: t("benefits-user-2"),
          description: t("benefits-user-2-desc"),
        },
        {
          title: t("benefits-user-3"),
          description: t("benefits-user-3-desc"),
        },
      ];
    }
    return [
      {
        title: t("benefits-employer-1"),
        description: t("benefits-employer-1-desc"),
      },
      {
        title: t("benefits-employer-2"),
        description: t("benefits-employer-2-desc"),
      },
      {
        title: t("benefits-employer-3"),
        description: t("benefits-employer-3-desc"),
      },
    ];
  };

  return (
    <section
      style={{
        backgroundColor: colorsObj.colorText,
        padding: "4rem 1.5rem",
      }}
    >
      <h2
        style={{
          color: colorsObj.colorBackground,
          fontSize: "2rem",
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: "2rem",
        }}
      >
        {t("benefits-title")}
      </h2>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          marginBottom: "2rem",
          position: "relative",
        }}
      >
        {BENEFIT_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                position: "relative",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "0.5rem 1.5rem",
                fontSize: "1rem",
                fontWeight: 600,
                color: isActive
                  ? colorsObj.colorPrimary
                  : colorsObj.colorOverlay + "80",
                transition: "color 0.3s ease",
              }}
            >
              {t(`benefits-tab-${tab}`)}

              {/* Underline */}
              <span
                style={{
                  position: "absolute",
                  left: "20%",
                  right: "20%",
                  bottom: 4,
                  height: 2,
                  backgroundColor: colorsObj.colorPrimary,
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? "scaleX(1)" : "scaleX(0)",
                  transformOrigin: "center",
                  transition: "transform 0.3s ease, opacity 0.3s ease",
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div
        className="w-full md:w-5/12 mx-auto"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "end",
          gap: "1rem",
        }}
      >
        <AnimatePresence mode="wait">
          {renderBenefits().map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              style={{
                padding: "1.5rem",
                borderRadius: "1rem",
                backgroundColor: colorsObj.colorOverlay + "20",
                WebkitBoxShadow: `0 4px 12px ${colorsObj.colorPrimary + "50"}`,
                color: colorsObj.colorBackground,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <h3 className="text-lg font-semibold">{benefit.title}</h3>
              <p className="mt-2 text-[#00000095]">{benefit.description}</p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}
